import { PlaceRef } from "@/types/entity"
import { DetailedPlace, EventParams } from "@/types/place"
import { Trip, TripRef } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { tripToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { isSerializedHttpError } from "./api/base-query"
import { placeApi } from "./api/place-api"
import type { AppDispatch } from "./redux-store"
import { initTrip } from "./trip-init-thunks"

// What a route does on the client once its entity is known, shared by the
// pages and the embedded app so the two cannot drift. A page passes the entity
// from its server props; the embedded app passes the ref.

/** Loads the trip into the trip slice and records the hub view. */
export const openTripRoute = createAsyncThunk<
  Trip,
  TripRef | Trip,
  { dispatch: AppDispatch }
>("routeInit/openTripRoute", async (tripOrRef, { dispatch }) => {
  const trip = await dispatch(initTrip(tripOrRef)).unwrap()

  sendAnalyticsEvent({
    name: "ViewTripHub",
    properties: tripToAmplitudeProperties(trip),
  })

  return trip
})

/** The detailed place, through the RTK cache; undefined when there is none. */
export const loadPlaceRoute = createAsyncThunk<
  DetailedPlace | undefined,
  { placeRef: PlaceRef; eventParams?: EventParams },
  { dispatch: AppDispatch }
>(
  "routeInit/loadPlaceRoute",
  async ({ placeRef, eventParams }, { dispatch }) => {
    const subscription = dispatch(
      placeApi.endpoints.getDetailedPlace.initiate({ placeRef, eventParams })
    )
    try {
      return await subscription.unwrap()
    } catch (error) {
      if (isSerializedHttpError(error, 404)) return undefined
      throw error
    } finally {
      subscription.unsubscribe()
    }
  }
)
